import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchSenderSubjects, type SenderSubject } from '@/api/senders';

interface AccountContext {
  accountId: string;
  folderId: string;
}

interface UseSenderSubjectsOptions {
  /** Array of account/folder contexts to fetch subjects from */
  contexts: AccountContext[];
  sender: { contactId?: string | null; email?: string | null };
  enabled?: boolean;
}

const subjectsCache = new Map<string, { subjects: SenderSubject[]; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Track in-flight fetches to avoid duplicate requests
const pendingFetches = new Map<string, Promise<SenderSubject[]>>();

/** Clear all cached subjects - call when entering grouped view to ensure fresh data */
export function clearSubjectsCache() {
  subjectsCache.clear();
}

function getCacheKey(contexts: AccountContext[], sender: { contactId?: string | null; email?: string | null }): string {
  const senderKey = sender.email || sender.contactId || '';
  const contextKeys = contexts
    .map((c) => `${c.accountId}:${c.folderId}`)
    .sort()
    .join('|');
  return `${contextKeys}:${senderKey}`;
}

export function useSenderSubjects({ contexts, sender, enabled = true }: UseSenderSubjectsOptions) {
  const senderPayload = useMemo(
    () => ({ contactId: sender.contactId, email: sender.email }),
    [sender.contactId, sender.email]
  );

  // Compute cache key for synchronous lookups and effect dependencies
  const cacheKey = useMemo(
    () => getCacheKey(contexts, senderPayload),
    [contexts, senderPayload]
  );

  // Initialize state from cache synchronously to avoid flash of empty content
  // This is especially important for virtualized lists where items remount frequently
  const [subjects, setSubjects] = useState<SenderSubject[]>(() => {
    if (!enabled || contexts.length === 0 || (!senderPayload.contactId && !senderPayload.email)) {
      return [];
    }
    const cached = subjectsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.subjects;
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track if component is still mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);

  // Sync subjects from cache when cache key changes (e.g., sender changes)
  // This handles the case where useState initializer doesn't re-run on prop changes
  useEffect(() => {
    if (!enabled || contexts.length === 0 || (!senderPayload.contactId && !senderPayload.email)) {
      setSubjects([]);
      return;
    }
    const cached = subjectsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setSubjects(cached.subjects);
    }
  }, [cacheKey, enabled, contexts.length, senderPayload.contactId, senderPayload.email]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || (!senderPayload.contactId && !senderPayload.email) || contexts.length === 0) {
      return () => { isMountedRef.current = false; };
    }

    const cached = subjectsCache.get(cacheKey);

    // Use cached data if available and fresh
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setSubjects(cached.subjects);
      return () => { isMountedRef.current = false; };
    }

    // Check if there's already a pending fetch for this cache key
    const pendingFetch = pendingFetches.get(cacheKey);
    if (pendingFetch) {
      setIsLoading(true);
      pendingFetch
        .then((results) => {
          if (isMountedRef.current) {
            setSubjects(results);
            setIsLoading(false);
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        });
      return () => { isMountedRef.current = false; };
    }

    setIsLoading(true);
    setError(null);

    // Create the fetch promise - DON'T abort on unmount so the cache gets populated
    const fetchPromise = (async () => {
      // Fetch from all contexts in parallel
      const results = await Promise.all(
        contexts.map((context) =>
          fetchSenderSubjects({
            accountId: context.accountId,
            folderId: context.folderId,
            sender: senderPayload,
            limit: 20,
          }).catch((err) => {
            // Don't fail the whole batch if one context fails
            console.warn('[useSenderSubjects] Failed to fetch from context:', context, err);
            return { subjects: [], totalMessages: 0 };
          })
        )
      );

      // Merge subjects from all contexts, deduplicating by email ID
      const seenIds = new Set<string>();
      const allSubjects: SenderSubject[] = [];
      results.forEach((result) => {
        result.subjects.forEach((subject) => {
          if (!seenIds.has(subject.id)) {
            seenIds.add(subject.id);
            allSubjects.push(subject);
          }
        });
      });

      // Sort by date descending
      const merged = allSubjects.sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
        const dateB = b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();
        return dateB - dateA;
      });

      // Cache the merged results (even if component unmounted)
      subjectsCache.set(cacheKey, {
        subjects: merged,
        timestamp: Date.now(),
      });

      return merged;
    })();

    // Track this as a pending fetch
    pendingFetches.set(cacheKey, fetchPromise);

    fetchPromise
      .then((merged) => {
        // Only update state if still mounted
        if (isMountedRef.current) {
          setSubjects(merged);
        }
      })
      .catch((err) => {
        if (isMountedRef.current) {
          setError(err);
          console.error('Failed to fetch sender subjects:', err);
        }
      })
      .finally(() => {
        // Clean up pending fetch tracker
        pendingFetches.delete(cacheKey);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });

    return () => {
      isMountedRef.current = false;
      // DON'T abort the fetch - let it complete and cache the result
    };
  }, [cacheKey, senderPayload, enabled, contexts]);

  return { subjects, isLoading, error };
}
